import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';
import {
  AuraIcon,
  AuraIconComponent,
  LinkDirective,
  RibbonDirective
} from '@com-delta-omnichannel/aura';
import { scrollToTop } from '@com-delta-omnichannel/aura-utils';
import { getNotificationIcon } from './adapters';
import { Notification } from './models/notification.interfaces';
import * as Utils from './utils';

@Component({
  selector: 'idp-notification',
  standalone: true,
  imports: [NgIf, NgClass, AuraIconComponent, RibbonDirective, LinkDirective],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss']
})
export class NotificationComponent implements AfterViewInit, OnDestroy {
  @Input() target = '';
  @Input() dismissAfter = 8000;
  @Output() updateTab: EventEmitter<number> = new EventEmitter<number>();

  notification: Notification | null = null;
  icon!: AuraIcon;

  private notificationElement!: HTMLElement | null;
  private observer: IntersectionObserver | null = null;
  private isFirstRender = true;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private elementRef: ElementRef<HTMLDivElement>,
    private renderer: Renderer2,
    private element: ElementRef
  ) {}

  @Input() set content(value: Notification) {
    this.init(value);
  }

  ngAfterViewInit() {
    if (this.notification) {
      this.init(this.notification);
    }
  }

  scrollTo(element: Element | null | undefined, event: Event) {
    // emit data to switch tab
    if ((this.notification?.tab as number) >= 0) {
      this.updateTab.emit(this.notification?.tab);
    }
    Utils.scrollTo(element, event);
  }

  init(notification: Notification) {
    this.notification = notification;
    this.icon = getNotificationIcon(notification.type);
    if (this._skipFirstRender()) {
      return;
    }

    const targetElement = Utils.findElement(this.target);

    if (this.target && targetElement) {
      this.notificationElement =
        this.elementRef.nativeElement.querySelector('.notification');

      this.observer = Utils.initObserver(
        targetElement,
        this.notificationElement,
        this._onIntersect.bind(this),
        this._toggleSticky.bind(this)
      );
    } else {
      scrollToTop();
    }

    Utils.startDismissTimer(
      notification.type,
      this.dismissAfter,
      this.hideTimeout,
      (timeout) => (this.hideTimeout = timeout),
      () => (this.notification = null)
    );
    this.validateFocus(notification.focusRequired);
  }

  validateFocus(focusRequired: boolean | undefined) {
    if (focusRequired !== false) this.focusNotification();
  }

  focusNotification() {
    setTimeout(() => {
      this.element.nativeElement.querySelector('#notification-focus')?.focus();
    }, 0);
  }

  ngOnDestroy() {
    this.isFirstRender = true;
    Utils.cleanupObserver(this.observer);
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  private _skipFirstRender(): boolean {
    if (this.isFirstRender && !this.target) {
      this.isFirstRender = false;
      return true;
    }
    return false;
  }

  private _onIntersect(entries: IntersectionObserverEntry[]) {
    const [entry] = entries;
    this._toggleSticky(!entry.isIntersecting);
  }

  private _toggleSticky(sticky: boolean) {
    Utils.toggleStickyClass(this.notificationElement, sticky, this.renderer);
  }
}
